/**
 * DeviceFormSheet
 * Sheet deslizable para crear o editar un dispositivo POS (terminal de caja).
 *  - Modo crear  → device prop omitido / null
 *  - Modo editar → device prop con el dispositivo existente
 *
 * En modo creación se piden: nombre, usuario, empresa y sucursal.
 * La contraseña se genera automáticamente en el cliente.
 * En modo edición: nombre y sucursal son editables; empresa y usuario son sólo-lectura.
 *
 * En modo editar incluye una zona de estado para activar o desactivar el terminal.
 * El estilo sigue el mismo patrón que ManagerFormSheet / BranchFormSheet.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Monitor,
  Save,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  KeyRound,
  RefreshCw,
  Copy,
  Check,
  ShieldAlert,
} from "lucide-react";
import {
  type Device,
  createDevice,
  updateDevice,
  resetDeviceCredentials,
} from "@/services/devicesService";
import { type Company } from "@/services/companiesService";
import { type Branch } from "@/services/branchesService";
import UserAvatar, { avatarGradients } from "@/components/UserAvatar";

interface DeviceFormSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Dispositivo a editar. Omitir o pasar null para modo creación. */
  device?: Device | null;
  /** Lista de empresas disponibles para el selector (modo creación). */
  allCompanies: Company[];
  /** Lista de todas las sucursales para filtrar por empresa seleccionada. */
  allBranches: Branch[];
  onSave: () => void;
  /**
   * Llamado SOLO tras crear un dispositivo nuevo, antes de que el sheet cierre.
   * Recibe todos los datos necesarios para el panel de credenciales.
   */
  onCreated?: (
    deviceName: string,
    username: string,
    password: string,
    companyId: string,
    branchId: string,
    backendUrl: string,
    dbPassword: string,
    syncToken: string,
  ) => void;
}

/** Genera una contraseña aleatoria segura de 16 caracteres para el dispositivo. */
function generateDevicePassword(): string {
  const chars =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&*";
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr)
    .map((b) => chars[b % chars.length])
    .join("");
}

/**
 * Genera un nombre de usuario a partir del primer segmento del UUID de empresa
 * y el primer segmento del UUID de sucursal, más 4 caracteres hex aleatorios
 * para garantizar unicidad entre varios dispositivos en la misma sucursal.
 * Ejemplo: "5c3c1676_abcd1234_3f2a"
 */
function generateDeviceUsername(companyId: string, branchId: string): string {
  const compPart   = companyId.split("-")[0] ?? companyId.slice(0, 8);
  const branchPart = branchId.split("-")[0]  ?? branchId.slice(0, 8);
  const randArr    = new Uint8Array(2);
  crypto.getRandomValues(randArr);
  const suffix = Array.from(randArr)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `${compPart}_${branchPart}_${suffix}`;
}

export default function DeviceFormSheet({
  isOpen,
  onClose,
  device,
  allCompanies,
  allBranches,
  onSave,
  onCreated,
}: DeviceFormSheetProps) {
  const isEditing = !!device;

  const [name, setName] = useState("");
  const [nameEdited, setNameEdited] = useState(false); // true cuando el usuario modifica el nombre manualmente
  const [username, setUsername] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  // Contraseña generada una sola vez al abrir en modo creación
  const [generatedPassword, setGeneratedPassword] = useState("");

  // Estado de la sección de credenciales
  type CredentialState = "idle" | "confirming" | "revealed";
  const [credentialState, setCredentialState] = useState<CredentialState>("idle");
  const [adminPassInput, setAdminPassInput] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Sucursales activas filtradas por la empresa seleccionada
  const availableBranches = allBranches.filter(
    (b) => b.company_id === companyId && b.is_active,
  );

  // Sync campos cuando cambia el dispositivo o se abre el panel
  useEffect(() => {
    if (isOpen) {
      setName(device?.name ?? "");
      setNameEdited(false);
      setUsername(device?.username ?? "");
      setCompanyId(device?.company_id ?? "");
      setBranchId(device?.branch_id ?? "");
      // Resetear sección de credenciales
      setCredentialState("idle");
      setAdminPassInput("");
      setNewPassword("");
      setIsCopied(false);
      // Generar contraseña una sola vez en modo creación
      if (!device) {
        setGeneratedPassword(generateDevicePassword());
      }
    }
  }, [isOpen, device]);

  // Al cambiar empresa, limpiar sucursal si ya no pertenece a ella
  useEffect(() => {
    if (branchId) {
      const stillValid = allBranches.some(
        (b) => b.id === branchId && b.company_id === companyId,
      );
      if (!stillValid) setBranchId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Auto-generar usuario cuando empresa Y sucursal están seleccionados (solo en creación)
  useEffect(() => {
    if (!isEditing && companyId && branchId) {
      setUsername(generateDeviceUsername(companyId, branchId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, branchId]);

  // Auto-generar nombre cuando empresa Y sucursal están seleccionados (solo en creación y si no fue editado)
  useEffect(() => {
    if (!isEditing && !nameEdited && companyId && branchId) {
      const companyName  = allCompanies.find((c) => c.id === companyId)?.name ?? "";
      const branchName   = allBranches.find((b) => b.id === branchId)?.name  ?? "";
      if (companyName && branchName) {
        const sanitize = (s: string) => s.toLowerCase().replace(/\s+/g, "_");
        setName(`pc-${sanitize(companyName)}-${sanitize(branchName)}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, branchId]);

  const handleClose = () => {
    if (isSaving || isToggling) return;
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("El nombre del dispositivo es obligatorio");
      return;
    }
    if (!isEditing && !username.trim()) {
      toast.error("El nombre de usuario es obligatorio");
      return;
    }
    if (!companyId) {
      toast.error("Debes seleccionar una empresa");
      return;
    }
    if (!branchId) {
      toast.error("Debes seleccionar una sucursal");
      return;
    }

    setIsSaving(true);
    try {
      if (isEditing) {
        await updateDevice(device.id, {
          name: name.trim(),
          branch_id: branchId,
        });
        toast.success("Dispositivo actualizado correctamente");
        onSave();
        onClose();
      } else {
        const finalUsername = username.trim();
        const finalPassword = generatedPassword || generateDevicePassword();
        const created = await createDevice({
          name: name.trim(),
          username: finalUsername,
          password: finalPassword,
          company_id: companyId,
          branch_id: branchId,
          is_active: true,
        });
        toast.success("Dispositivo creado correctamente");
        onSave();
        onClose();
        // Abrir modal de credenciales DESPUÉS de cerrar el sheet
        const backendUrl = allBranches.find((b) => b.id === branchId)?.backend_url ?? "";
        onCreated?.(
          name.trim(),
          finalUsername,
          finalPassword,
          companyId,
          branchId,
          backendUrl,
          created.db_password ?? "",
          created.sync_token ?? "",
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al guardar el dispositivo",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!device) return;
    setIsToggling(true);
    try {
      await updateDevice(device.id, { is_active: !device.is_active });
      toast.success(
        device.is_active
          ? "Dispositivo dado de baja correctamente"
          : "Dispositivo reactivado correctamente",
      );
      onSave();
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al cambiar el estado",
      );
    } finally {
      setIsToggling(false);
    }
  };

  const handleResetCredentials = async () => {
    if (!device || !adminPassInput.trim()) {
      toast.error("Ingresa tu contraseña de administrador");
      return;
    }
    setIsResetting(true);
    try {
      const plain = await resetDeviceCredentials(device.id, adminPassInput.trim());
      setNewPassword(plain);
      setAdminPassInput("");
      setCredentialState("revealed");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al regenerar credenciales");
    } finally {
      setIsResetting(false);
    }
  };

  const handleCopyPassword = () => {
    if (!newPassword) return;

    const copyFallback = () => {
      const ta = document.createElement("textarea");
      ta.value = newPassword;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    };

    if (navigator.clipboard) {
      navigator.clipboard.writeText(newPassword).catch(copyFallback);
    } else {
      copyFallback();
    }

    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-slate-900 border-white/10 text-white p-0 flex flex-col h-[100dvh] overflow-hidden"
      >
        {/* Header */}
        <SheetHeader
          className="flex-shrink-0 px-6 pb-4 border-b border-white/10"
          style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }}
        >
          <div className="flex items-center gap-4">
            {isEditing ? (
              <UserAvatar role="device" size="xl" icon={<Monitor className="h-7 w-7" />} />
            ) : (
              <div
                className={`w-14 h-14 rounded-2xl ${avatarGradients.device} flex items-center justify-center text-white flex-shrink-0`}
              >
                <Monitor className="h-7 w-7" />
              </div>
            )}
            <div className="min-w-0">
              <SheetTitle className="text-white text-lg font-semibold leading-tight">
                {isEditing ? "Editar dispositivo" : "Nuevo dispositivo"}
              </SheetTitle>
              {isEditing && (
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <p className="text-sm text-white/50 font-mono">@{device.username}</p>
                  <Badge
                    className={
                      device.is_active
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        : "bg-red-500/20 text-red-400 border-red-500/30"
                    }
                  >
                    {device.is_active ? "Activo" : "Inactivo"}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Cuerpo con scroll */}
        <ScrollArea className="flex-1 min-h-0 px-6 py-5">
          <div className="space-y-6">

            {/* Asignación */}
            <section className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                Asignación
              </p>

              {/* Empresa — sólo-lectura en edición */}
              {isEditing ? (
                <div className="space-y-1">
                  <p className="text-xs text-white/40">Empresa</p>
                  <p className="text-sm text-white/80 font-medium">{device.company_name}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="device-company" className="text-white/70 text-sm">
                    Empresa <span className="text-red-400">*</span>
                  </Label>
                  <Select
                    value={companyId}
                    onValueChange={setCompanyId}
                    disabled={isSaving || isToggling}
                  >
                    <SelectTrigger
                      id="device-company"
                      className="bg-white/5 border-white/20 text-white focus:ring-0 focus:border-white/40"
                    >
                      <SelectValue placeholder="Seleccionar empresa..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-white/10 text-white">
                      {allCompanies
                        .filter((c) => c.is_active)
                        .map((c) => (
                          <SelectItem
                            key={c.id}
                            value={c.id}
                            className="focus:bg-white/10 focus:text-white"
                          >
                            {c.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Sucursal — filtrada por empresa */}
              <div className="space-y-2">
                <Label htmlFor="device-branch" className="text-white/70 text-sm">
                  Sucursal <span className="text-red-400">*</span>
                </Label>
                <Select
                  value={branchId}
                  onValueChange={setBranchId}
                  disabled={!companyId || isSaving || isToggling}
                >
                  <SelectTrigger
                    id="device-branch"
                    className="bg-white/5 border-white/20 text-white focus:ring-0 focus:border-white/40 disabled:opacity-50"
                  >
                    <SelectValue
                      placeholder={
                        companyId
                          ? "Seleccionar sucursal..."
                          : "Primero selecciona una empresa"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-white/10 text-white">
                    {availableBranches.length === 0 ? (
                      <div className="py-3 px-2 text-sm text-white/40 text-center">
                        Sin sucursales activas
                      </div>
                    ) : (
                      availableBranches.map((b) => (
                        <SelectItem
                          key={b.id}
                          value={b.id}
                          className="focus:bg-white/10 focus:text-white"
                        >
                          {b.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </section>

            <Separator className="bg-white/10" />

            {/* Datos del dispositivo */}
            <section className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                Datos del dispositivo
              </p>

              {/* Nombre */}
              <div className="space-y-2">
                <Label htmlFor="device-name" className="text-white/70 text-sm">
                  Nombre <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="device-name"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setNameEdited(true); }}
                  placeholder={
                    !isEditing && !branchId
                      ? "Selecciona empresa y sucursal primero"
                      : "Ej: pc-empresa-sucursal"
                  }
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                             focus:bg-white/10 focus:border-white/40 disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={(!isEditing && !branchId) || isSaving || isToggling}
                />
                {!isEditing && (
                  <p className="text-xs text-white/30">
                    {nameEdited
                      ? "Nombre personalizado — se usará tal cual."
                      : branchId
                        ? "Generado desde empresa y sucursal. Puedes editarlo libremente."
                        : "Se habilitará automáticamente al seleccionar empresa y sucursal."}
                  </p>
                )}
              </div>

              {/* Usuario — solo lectura en edición */}
              {isEditing && (
                <div className="space-y-2">
                  <Label htmlFor="device-username" className="text-white/70 text-sm">
                    Usuario
                  </Label>
                  <div className="flex items-center gap-2 bg-white/5 border border-white/15 rounded-xl px-3 py-2.5">
                    <span className="font-mono text-sm text-white/60 select-all">
                      @{device.username}
                    </span>
                  </div>
                  <p className="text-xs text-white/30">
                    El nombre de usuario no se puede modificar.
                  </p>
                </div>
              )}
            </section>

            {/* Zona de credenciales + estado — solo en edición */}
            {isEditing && (
              <>
                <Separator className="bg-white/10" />

                {/* Credenciales del terminal */}
                <section className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                    <KeyRound className="inline-block h-3 w-3 mr-1 mb-0.5" />
                    Credenciales del terminal
                  </p>

                  {credentialState === "idle" && (
                    <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <ShieldAlert className="h-5 w-5 text-amber-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-white">Contraseña del dispositivo</p>
                          <p className="text-xs text-white/40">
                            Las contraseñas se almacenan cifradas. Para obtener una nueva,
                            regenera las credenciales. Esto cambiará la contraseña actual.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 border border-white/10">
                        <span className="text-sm text-white/50 font-mono">@{device.username}</span>
                        <span className="text-sm text-white/30 tracking-widest">••••••••••••</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCredentialState("confirming")}
                        disabled={isSaving || isToggling}
                        className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 bg-transparent"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Regenerar contraseña
                      </Button>
                    </div>
                  )}

                  {credentialState === "confirming" && (
                    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <ShieldAlert className="h-4 w-4 text-amber-400 flex-shrink-0" />
                        <p className="text-sm text-amber-300 font-medium">
                          Confirma tu identidad
                        </p>
                      </div>
                      <p className="text-xs text-white/50">
                        Ingresa tu contraseña de administrador para continuar.
                        La contraseña actual del dispositivo quedará invalidada.
                      </p>
                      <Input
                        type="password"
                        value={adminPassInput}
                        onChange={(e) => setAdminPassInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleResetCredentials()}
                        placeholder="Tu contraseña de administrador"
                        autoFocus
                        className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                                   focus:bg-white/10 focus:border-amber-400/50"
                        disabled={isResetting}
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setCredentialState("idle"); setAdminPassInput(""); }}
                          disabled={isResetting}
                          className="flex-1 border-white/20 text-white/60 hover:bg-white/10 bg-transparent"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Cancelar
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleResetCredentials}
                          disabled={isResetting || !adminPassInput.trim()}
                          className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          {isResetting
                            ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            : <RefreshCw className="h-4 w-4 mr-1" />}
                          Confirmar
                        </Button>
                      </div>
                    </div>
                  )}

                  {credentialState === "revealed" && (
                    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                        <p className="text-sm text-emerald-300 font-medium">
                          Nueva contraseña generada
                        </p>
                      </div>
                      <p className="text-xs text-white/50">
                        Copia esta contraseña ahora. No podrás verla de nuevo sin regenerarla.
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-white/10 rounded-lg px-3 py-2 border border-white/20">
                          <span className="text-sm text-white font-mono tracking-wider select-all">
                            {newPassword}
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCopyPassword}
                          className="border-white/20 text-white/70 hover:bg-white/10 bg-transparent flex-shrink-0"
                        >
                          {isCopied
                            ? <Check className="h-4 w-4 text-emerald-400" />
                            : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setCredentialState("idle"); setNewPassword(""); }}
                        className="w-full border-white/20 text-white/60 hover:bg-white/10 bg-transparent"
                      >
                        Ocultar
                      </Button>
                    </div>
                  )}
                </section>

                <Separator className="bg-white/10" />

                {/* Estado del terminal */}
                <section className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                    Estado del terminal
                  </p>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      {device.is_active ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white">
                          {device.is_active ? "Terminal activo" : "Terminal inactivo"}
                        </p>
                        <p className="text-xs text-white/40">
                          {device.is_active
                            ? "El dispositivo puede sincronizarse normalmente."
                            : "El dispositivo no puede conectarse al sistema."}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleToggleActive}
                      disabled={isSaving || isToggling}
                      className={
                        device.is_active
                          ? "w-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 bg-transparent"
                          : "w-full border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 bg-transparent"
                      }
                    >
                      {isToggling ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : device.is_active ? (
                        <AlertTriangle className="h-4 w-4 mr-2" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                      )}
                      {device.is_active ? "Dar de baja" : "Reactivar"}
                    </Button>
                  </div>
                </section>
              </>
            )}
          </div>
        </ScrollArea>

        {/* Footer fijo */}
        <SheetFooter
          className="flex-shrink-0 flex flex-row justify-center sm:justify-end gap-2 px-6 py-4
                     border-t border-white/10 bg-slate-900"
        >
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSaving || isToggling}
            className="border-white/20 text-white/70 hover:bg-white/10 hover:text-white bg-transparent"
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isToggling}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {isEditing ? "Guardar cambios" : "Crear dispositivo"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
